import CheckCircleIcon from 'components/ReusableComponents/Icons/CheckCircle'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import PendingIcon from 'components/ReusableComponents/Icons/PendingIcon'
import { PMSsWithSyncLoginSupport } from 'constants'
import usePhoneAvailability from 'hooks/usePhoneAvailability'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'

import { onboardingIssues, onboardingStatusTitles, titles } from './constants'
import { evaluateOnboardingStatuses } from './evaluateOnboardingStatuses'
import styles from './OnboardingStatuses.module.scss'

const OnboardingStatuses = ({
  id,
  pms,
  lastAutomatedInfoSyncOn,
  setShowingTaxDetailsDialog,
  setHostingMessagingDialogVisible,
  setShowingEditTAsettingsDialog,
  phoneNumbers,
  reservationHotLine,
  pendingRequests,
  invoiceAddress,
  billingAddress,
  streetAddress,
  subscriptions,
  tcrCampaignId,
  status,
  hotelConfigData,
  hotelConfigStatus,
  hasHostedMessagesSet
}) => {
  const history = useHistory()
  const { addToast } = useToasts()
  const shouldRedirectPMSLogin = PMSsWithSyncLoginSupport.includes(pms)
  const { isDisabled, disabledReason } = usePhoneAvailability({
    phoneNumbers,
    reservationHotLine,
    pendingRequests,
    invoiceAddress,
    billingAddress,
    streetAddress,
    subscriptions,
    tcrCampaignId,
    status
  })

  const checkParameters = {
    campaignRegistry: hotelConfigData.campaignRegistry,
    isPmsSyncing: hotelConfigData.pmsSync,
    tripAdvisor: hotelConfigData.tripadvisorSetup,
    reviewSettings: hotelConfigData.reviewSettingConfiguration,
    bookingEngine: hotelConfigData.beSetup,
    billingInvoiceAdress: hotelConfigData.billingAndInvoiceAddress,
    hostedMessaging: hotelConfigData.hostedMessaging,
    phoneNumber: hotelConfigData.telcoSetup,
    logoAndcolors: hotelConfigData.brandLogoAndColours,
    chatbotResponses: hotelConfigData.optionalInfoSteps.chatBotResponses,
    otaPromo: hotelConfigData.optionalInfoSteps.promotionSetup.ota,
    directPromo: hotelConfigData.optionalInfoSteps.promotionSetup.direct,
    users: hotelConfigData.optionalInfoSteps.addedUsersCount,
    enabledCampaigns: hotelConfigData.optionalInfoSteps.configureAndEnableCampaignsCount,
    marketingURL: hotelConfigData.optionalInfoSteps.textMarketingUrLKey,
    domain: hotelConfigData.optionalInfoSteps.landingPageDomainSettingsUrL
  }

  const handleEditClick = title => {
    if (
      title === titles.tripAdvisor ||
      title === titles.bookingEngine ||
      title === titles.billingInvoiceAdress
    ) {
      history.push(`/editHotel/${id}/${lastAutomatedInfoSyncOn ? 'limited' : ''}`)
    }
    if (title === titles.campaignRegistry) {
      setShowingTaxDetailsDialog(true)
    }
    if (title === titles.hostedMessaging) {
      setHostingMessagingDialogVisible(true)
    }
    if (title === titles.reviewSettings) {
      setShowingEditTAsettingsDialog(true)
    }
    if (title === titles.pmsSync) {
      if (shouldRedirectPMSLogin) {
        history.push('/pms-login')
      }
    }
    if (title === titles.phoneNumber) {
      if (isDisabled) {
        addToast(`${disabledReason}`, {
          appearance: 'error',
          autoDismiss: true
        })
      } else {
        history.push(`/addTelecom/${id}`)
      }
    }
  }
  const generateOnboardingStatuses = checkParameters => {
    return onboardingIssues.map(onboardingGroup => {
      const onboardingIssuesWithStatus = onboardingGroup.onboardingIssues.map(
        onboarding => {
          const onboardingStatus = evaluateOnboardingStatuses({
            title: onboarding.title,
            checkParameters,
            shouldRedirectPMSLogin,
            hasHostedMessagesSet
          })
          return {
            title: onboarding.title,
            status: onboardingStatus.status,
            value: onboardingStatus.value
          }
        }
      )

      return {
        onboardingGroupTitle: onboardingGroup.onboardingGroupTitle,
        onboardingIssues: onboardingIssuesWithStatus
      }
    })
  }

  const OnboardingRow = ({ title, status, value }) => {
    const hasActivePhoneNumber = phoneNumbers.some(
      phoneNumber => phoneNumber.status === 'Active'
    )
    const isHostedMessagingDisabled =
      title === titles.hostedMessaging && !hasActivePhoneNumber

    return (
      <div
        className={styles.onboardingRow}
        data-tip={value}
        data-for="onboardingStatusTooltip"
      >
        <div className={styles.onboardingTitle}>{title}</div>
        <div className={styles.onboardingStatus}>
          {status === 'Ok' && <CheckCircleIcon className={styles.checkIcon} />}
          {status === 'Pending' && <PendingIcon className={styles.pendingIcon} />}
          {status === 'Not set' && <CrossIcon className={styles.crossIcon} />}
          {status === 'None' && <p className={styles.optionalValue}>{value}</p>}
          {(status === 'Edit' || status === 'Set up') && (
            <button
              className={
                isHostedMessagingDisabled ? styles.disabledEdit : styles.editValue
              }
              onClick={() => handleEditClick(title)}
              disabled={isHostedMessagingDisabled}
              data-tip={
                isHostedMessagingDisabled
                  ? 'You need an active phone number to enable hosted messaging'
                  : ''
              }
              data-for="hostedMessagingTooltip"
            >
              {status === 'Edit' ? 'Edit' : 'Set up'}
            </button>
          )}
        </div>
        {isHostedMessagingDisabled && (
          <ReactTooltip
            id="hostedMessagingTooltip"
            place="right"
            type="dark"
            effect="solid"
          />
        )}
      </div>
    )
  }

  return (
    <div className={styles.onboardingStatuses}>
      <div className={styles.titleHolder}>
        <ReactTooltip
          id="onboardingStatusTooltip"
          place="right"
          type="dark"
          effect="solid"
        />
        <p className={styles.boxTitle}>Onboarding status</p>
        <p className={styles.statusTitle}>
          {hotelConfigStatus && onboardingStatusTitles[hotelConfigStatus.currentStage]}
        </p>
      </div>
      <div className={styles.onboardingIssuesHolder}>
        {generateOnboardingStatuses(checkParameters).map(
          ({ onboardingGroupTitle, onboardingIssues }) => (
            <div key={onboardingGroupTitle} className={styles.onboardingGroup}>
              {onboardingGroupTitle && (
                <div className={styles.onboardingGroupTitle}>{onboardingGroupTitle}</div>
              )}
              {onboardingIssues.map(({ title, status, value }) => (
                <OnboardingRow key={title} title={title} status={status} value={value} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default OnboardingStatuses
